// New project and Edit project (Block J, UI-22: on the v2 kit's Modal).
//
// Block J fix: it closed the moment Save was pressed, before the server answered, so a refused save
// lost what was typed and its message (projectSaveFailure) landed in a dialog that had already gone.
// It now waits for onSave (which throws on a refusal) and stays open with the reason.
import React, { useState } from 'react';
import { useWorkspaceRoster } from '../../hooks/useWorkspaceRoster';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { personalFlag } from '../../nav/views';
import WorkflowEditor from '../WorkflowEditor';
import { messageFromError } from '../../utils/apiError';
import { Button, Checkbox, DateInput, Field, Input, Modal, MoneyInput, SegmentedControl, Select, Textarea } from '../../ui';
import '../Projects/projects.css';

// The project's own colour, the owner's choice: stored on the project and shown as its dot. These
// are data values (the twelve as before, the repeated green replaced by aqua), not the app's
// interface colours.
const COLORS = [
  '#5352ed', '#27ae60', '#e74c3c', '#f39c12',
  '#0C2D48', '#8e44ad', '#16a085', '#d35400',
  '#2980b9', '#c0392b', '#1abc9c', '#E4E69B',
];
const COLOR_NAMES = ['Violet', 'Green', 'Red', 'Amber', 'Navy', 'Purple', 'Teal', 'Burnt orange', 'Blue', 'Crimson', 'Aqua', 'Lime'];

const INTERNAL_CATEGORIES = [
  'Admin', 'Finance', 'HR/Team', 'Marketing/Socials',
  'Operations', 'Product', 'Other',
];
const CUSTOM = '__custom__';

// UI-24 item 17: the form is made from the project as it opens (keyed by the project), never filled
// in by an effect afterwards, so its first frame is that project's and never the previous one's.
export default function CreateProjectModal(props) {
  if (!props.isOpen) return null;
  const { editProject } = props;
  return <ProjectForm key={editProject ? `edit-${editProject.id}` : 'new'} {...props} />;
}

const EMPTY = {
  name: '', client: '', status: 'active',
  color: COLORS[0], deadline: '', startDate: '',
  budget: '', description: '', team: [],
  projectType: 'client', category: '',
};

const formFrom = (editProject) => {
  if (!editProject) return EMPTY;
  const type = editProject.projectType || editProject.project_type || 'client';
  return {
    name:        editProject.name        || '',
    client:      editProject.client      || '',
    status:      editProject.status      || 'active',
    color:       editProject.color       || COLORS[0],
    deadline:    editProject.deadline    || '',
    startDate:   editProject.startDate   || editProject.start_date || '',
    budget:      editProject.budget != null ? String(editProject.budget) : '',
    description: editProject.description || '',
    team:        editProject.team        || [],
    projectType: type,
    category:    editProject.category    || '',
  };
};

function ProjectForm({ onClose, onSave, editProject = null, saveFailure = '' }) {
  const { members: allUsers } = useWorkspaceRoster();
  // Decision 3: a project's team is who may see it in a business. A personal project is its
  // creator's only, so there is no team to pick there (and the personal roster's family members have
  // no accounts to give access to).
  const { activeWorkspaceId, workspaces } = useCurrentUser() || {};
  const teamPickable = personalFlag(workspaces, activeWorkspaceId) !== true;
  const teamPeople = teamPickable ? allUsers.filter(u => u.userId) : [];

  // Which type is chosen
  const [projectTab, setProjectTab] = useState(() => formFrom(editProject).projectType);
  const [form, setForm]   = useState(() => formFrom(editProject));
  const [error, setError] = useState('');
  const [failure, setFailure] = useState('');
  const [saving, setSaving] = useState(false);
  const [customCat, setCustomCat] = useState('');
  const [workflowDraft, setWorkflowDraft] = useState({ stages: [], columns: [] });

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const toggleMember = (id) => {
    set('team', form.team.includes(id)
      ? form.team.filter(x => x !== id)
      : [...form.team, id]
    );
  };

  const handleTabSwitch = (tab) => {
    setProjectTab(tab);
    set('projectType', tab);
    // Clear cross-type fields
    if (tab === 'internal') set('client', '');
    if (tab === 'client')   set('category', '');
  };

  const close = () => { if (!saving) onClose(); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Give the project a name.'); return; }
    const category = projectTab === 'internal'
      ? (customCat.trim() || (form.category === CUSTOM ? '' : form.category) || 'Other')
      : '';
    setSaving(true);
    setFailure('');
    try {
      await onSave({
        name:          form.name.trim(),
        client:        projectTab === 'client' ? form.client.trim() : '',
        status:        form.status,
        color:         form.color,
        deadline:      form.deadline,
        startDate:     form.startDate,
        budget:        form.budget ? parseFloat(form.budget) : null,
        description:   form.description.trim(),
        // Saved to project_members: the people on it see it (decision 3). Not sent in Personal.
        ...(teamPickable && { team: form.team }),
        projectType:   projectTab,
        category,
        workflowDraft,
      });
    } catch (err) {
      setFailure(messageFromError(err, 'That project was not saved.'));
      setSaving(false);
      return;
    }
    setSaving(false);
    onClose();
  };

  const isInternal = projectTab === 'internal';
  const shownFailure = failure || saveFailure;

  const footer = (
    <div className="pj-foot">
      {/* Beside the button that failed: a failure from the server is not one she can see while typing. */}
      {shownFailure && (
        <p data-testid="project-save-failed" className="pj-failed" role="alert">{shownFailure}</p>
      )}
      <Button variant="tertiary" onClick={close} disabled={saving}>Cancel</Button>
      <Button variant="confirm" onClick={handleSave} disabled={saving} aria-busy={saving || undefined}>
        {saving ? 'Saving…' : editProject ? 'Save changes' : isInternal ? 'Create internal project' : 'Create client project'}
      </Button>
    </div>
  );

  return (
    <Modal open onClose={close} title={editProject ? 'Edit project' : 'New project'} color="pastel-blue" width={620} footer={footer}>
      <div className="pj-modal-form">
        {!editProject && (
          <SegmentedControl label="Project type" value={projectTab} onChange={handleTabSwitch}
            options={[{ value: 'client', label: '💼 Client project' }, { value: 'internal', label: '📁 Internal project' }]} />
        )}

        <Field label="Project name" required error={error || undefined}>
          <Input data-autofocus value={form.name} placeholder="e.g. Brand Refresh"
            onChange={e => { set('name', e.target.value); if (error) setError(''); }} />
        </Field>

        {!isInternal && (
          <Field label="Client">
            <Input value={form.client} onChange={e => set('client', e.target.value)} placeholder="Client name or company" />
          </Field>
        )}

        {isInternal && (
          <Field label="Category">
            <Select value={form.category}
              onChange={e => { set('category', e.target.value); if (e.target.value !== CUSTOM) setCustomCat(''); }}
              options={[
                { value: '', label: 'Select category…' },
                ...INTERNAL_CATEGORIES.map(c => ({ value: c, label: c })),
                ...(form.category && form.category !== CUSTOM && !INTERNAL_CATEGORIES.includes(form.category) ? [{ value: form.category, label: form.category }] : []),
                { value: CUSTOM, label: 'Other (type below)…' },
              ]} />
          </Field>
        )}
        {isInternal && (form.category === CUSTOM || (form.category === '' && customCat)) && (
          <Field label="Your category">
            <Input autoFocus value={customCat} onChange={e => setCustomCat(e.target.value)} placeholder="Custom category name…" />
          </Field>
        )}

        <Field label="Status">
          <Select value={form.status} onChange={e => set('status', e.target.value)}
            options={[{ value: 'active', label: 'Active' }, { value: 'on hold', label: 'On hold' }, { value: 'completed', label: 'Completed' }]} />
        </Field>

        <div className="pj-pair">
          <Field label="Start date">
            <DateInput value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </Field>
          <Field label="Due date">
            <DateInput value={form.deadline} onChange={e => set('deadline', e.target.value)} />
          </Field>
        </div>

        {!isInternal && (
          <Field label="Budget (optional)">
            <MoneyInput value={form.budget} onChange={e => set('budget', e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" />
          </Field>
        )}

        <Field label="Description">
          <Textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What is this project about?" />
        </Field>

        <div className="pj-drawer__section" role="radiogroup" aria-label="Colour">
          <span className="pj-drawer__label">Colour</span>
          <div className="pj-swatches">
            {COLORS.map((c, i) => (
              <button key={c} type="button" className="pj-swatch" role="radio" aria-checked={form.color === c}
                aria-label={COLOR_NAMES[i]} style={{ '--pj-dot': c }} onClick={() => set('color', c)}>
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>

        {teamPeople.length > 0 && (
          <div className="pj-drawer__section" role="group" aria-label="Team members">
            <span className="pj-drawer__label">Team members</span>
            <p className="pj-muted">They see this project and can work on it. You, the owner and admins always can.</p>
            <div className="pj-check-list">
              {teamPeople.map(u => (
                <Checkbox key={u.id} label={u.name} checked={form.team.includes(u.id)} onChange={() => toggleMember(u.id)} />
              ))}
            </div>
          </div>
        )}

        {/* Workflow: only on create (not edit) */}
        {!editProject && (
          <div className="pj-drawer__section">
            <span className="pj-drawer__label">Workflow</span>
            <p className="pj-muted">Define how work moves through this project.</p>
            <WorkflowEditor
              mode="project"
              entityId={null}
              showColumns={true}
              showAutomations={false}
              onChange={setWorkflowDraft}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
